import { useCallback, useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import styled from 'styled-components';
import Button from './ui/Button';
import { MensajeError } from './ui/Form';

const Contenedor = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
`;

const Visor = styled.div`
  position: relative;
  width: 100%;
  max-width: 320px;
  aspect-ratio: 1;
  border-radius: ${({ theme }) => theme.radii.md};
  overflow: hidden;
  background: #000;
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Marco = styled.div`
  position: absolute;
  inset: 14%;
  border: 3px solid ${({ theme }) => theme.colors.sage};
  border-radius: ${({ theme }) => theme.radii.sm};
  pointer-events: none;
`;

// Reutiliza el mismo código un ratito para no disparar el mismo QR una y otra vez
// mientras el cliente todavía lo tiene frente a la cámara.
const VENTANA_REPETICION_MS = 4000;

// Escanea códigos QR con la cámara del dispositivo (o webcam) y avisa cada vez que detecta uno nuevo.
function QrScanner({ onDetectado, deshabilitado }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(null);
  const ultimoCodigo = useRef('');
  const ultimaDeteccion = useRef(0);
  const onDetectadoRef = useRef(onDetectado);
  const [activo, setActivo] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    onDetectadoRef.current = onDetectado;
  }, [onDetectado]);

  if (!canvasRef.current) {
    canvasRef.current = document.createElement('canvas');
  }

  const detener = useCallback(() => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setActivo(false);
  }, []);

  const tick = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) {
      frameRef.current = requestAnimationFrame(tick);
      return;
    }
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imagen = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const resultado = jsQR(imagen.data, imagen.width, imagen.height);

    if (resultado && resultado.data) {
      const ahora = Date.now();
      const esRepetido =
        resultado.data === ultimoCodigo.current && ahora - ultimaDeteccion.current < VENTANA_REPETICION_MS;
      if (!esRepetido) {
        ultimoCodigo.current = resultado.data;
        ultimaDeteccion.current = ahora;
        onDetectadoRef.current(resultado.data);
      }
    }
    frameRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (!activo || !streamRef.current || !videoRef.current) return undefined;
    const video = videoRef.current;
    video.srcObject = streamRef.current;
    video.play().catch(() => {});
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [activo, tick]);

  const iniciar = useCallback(async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setActivo(true);
    } catch (e) {
      setError('No se pudo acceder a la cámara. Revisa los permisos del navegador o usa el código manual.');
    }
  }, []);

  useEffect(() => () => detener(), [detener]);

  return (
    <Contenedor>
      {error && <MensajeError role="alert">{error}</MensajeError>}
      <Visor $visible={activo}>
        <video ref={videoRef} muted playsInline aria-label="Vista de la cámara para escanear el QR" />
        <Marco aria-hidden="true" />
      </Visor>
      {!activo ? (
        <Button type="button" onClick={iniciar} $variante="secundario" disabled={deshabilitado}>
          Escanear con cámara
        </Button>
      ) : (
        <Button type="button" onClick={detener} $variante="fantasma">
          Detener cámara
        </Button>
      )}
    </Contenedor>
  );
}

export default QrScanner;
